import {
  GroupDoc,
  GroupInheritanceMap,
  ParentGroupLinkMode,
  ParentGroupLinkValue
} from 'types/api/changes'
import { InheritFromGroupsRequest } from 'types/api/group'
import { normalizeGroupInheritanceMap } from 'utils/calendarInheritance'

export type CalendarGraph = Record<string, GroupInheritanceMap>

export interface CalendarGraphEdge {
  id: string
  childId: string
  parentId: string
  source: string
  target: string
}

export type CalendarEdgeHandle = 'top' | 'right' | 'bottom' | 'left'

interface EdgeCenter {
  x: number
  y: number
}

export function graphFromGroups(groups: GroupDoc[]): CalendarGraph {
  return Object.fromEntries(
    groups
      .filter((group): group is GroupDoc & { _id: string } =>
        Boolean(group._id)
      )
      .map((group) => [
        group._id,
        normalizeGroupInheritanceMap(group.ParentGroups)
      ])
  )
}

export function graphEdges(graph: CalendarGraph): CalendarGraphEdge[] {
  return Object.entries(graph).flatMap(([childId, parentGroups]) =>
    Object.keys(normalizeGroupInheritanceMap(parentGroups)).map((parentId) => ({
      id: `${parentId}->${childId}`,
      childId,
      parentId,
      source: parentId,
      target: childId
    }))
  )
}

function pickHandlePairByAxis(
  sourceCenter: EdgeCenter,
  targetCenter: EdgeCenter,
  axis: 'horizontal' | 'vertical'
): { source: CalendarEdgeHandle; target: CalendarEdgeHandle } {
  if (axis === 'horizontal') {
    return sourceCenter.x <= targetCenter.x
      ? { source: 'right', target: 'left' }
      : { source: 'left', target: 'right' }
  }

  return sourceCenter.y <= targetCenter.y
    ? { source: 'bottom', target: 'top' }
    : { source: 'top', target: 'bottom' }
}

export function graphEdgeHandles(
  sourceId: string,
  targetId: string,
  sourceCenter?: EdgeCenter,
  targetCenter?: EdgeCenter,
  reverseEdgeExists = false
): { source: CalendarEdgeHandle; target: CalendarEdgeHandle } {
  if (!sourceCenter || !targetCenter) {
    return { source: 'bottom', target: 'top' }
  }

  const dx = targetCenter.x - sourceCenter.x
  const dy = targetCenter.y - sourceCenter.y
  const primaryAxis: 'horizontal' | 'vertical' =
    Math.abs(dx) > Math.abs(dy) ? 'horizontal' : 'vertical'
  const axis =
    reverseEdgeExists && sourceId > targetId
      ? primaryAxis === 'horizontal'
        ? 'vertical'
        : 'horizontal'
      : primaryAxis

  return pickHandlePairByAxis(sourceCenter, targetCenter, axis)
}

export function isSelfConnection(childId: string, parentId: string) {
  return childId === parentId
}

export function bidirectionalEdgePath(
  source: EdgeCenter,
  target: EdgeCenter,
  sourcePosition: CalendarEdgeHandle,
  targetPosition: CalendarEdgeHandle
): [string, number, number] {
  const dx = target.x - source.x
  const dy = target.y - source.y
  const distance = Math.hypot(dx, dy)
  const offsetX = distance > 0 ? (-dy / distance) * 96 : 96
  const offsetY = distance > 0 ? (dx / distance) * 96 : 0
  const handleDirections: Record<CalendarEdgeHandle, EdgeCenter> = {
    top: { x: 0, y: -1 },
    right: { x: 1, y: 0 },
    bottom: { x: 0, y: 1 },
    left: { x: -1, y: 0 }
  }
  const sourceDirection = handleDirections[sourcePosition]
  const targetDirection = handleDirections[targetPosition]
  const sourceControl = {
    x: source.x + sourceDirection.x * 120 + offsetX,
    y: source.y + sourceDirection.y * 120 + offsetY
  }
  const targetControl = {
    x: target.x + targetDirection.x * 120 + offsetX,
    y: target.y + targetDirection.y * 120 + offsetY
  }

  return [
    `M${source.x},${source.y} C${sourceControl.x},${sourceControl.y} ${targetControl.x},${targetControl.y} ${target.x},${target.y}`,
    (source.x + 3 * sourceControl.x + 3 * targetControl.x + target.x) / 8,
    (source.y + 3 * sourceControl.y + 3 * targetControl.y + target.y) / 8
  ]
}

export function addParentConnection(
  graph: CalendarGraph,
  childId: string,
  parentId: string
): CalendarGraph {
  if (isSelfConnection(childId, parentId)) return graph
  const parents = normalizeGroupInheritanceMap(graph[childId])
  if (parents[parentId]) return graph

  return {
    ...graph,
    [childId]: {
      ...parents,
      [parentId]: ParentGroupLinkMode.Propagating
    }
  }
}

export function setParentConnectionMode(
  graph: CalendarGraph,
  childId: string,
  parentId: string,
  mode: ParentGroupLinkValue
): CalendarGraph {
  const parents = normalizeGroupInheritanceMap(graph[childId])
  if (!parents[parentId] || parents[parentId] === mode) return graph

  return { ...graph, [childId]: { ...parents, [parentId]: mode } }
}

export function removeParentConnection(
  graph: CalendarGraph,
  childId: string,
  parentId: string
): CalendarGraph {
  const childParents = { ...normalizeGroupInheritanceMap(graph[childId]) }
  const removedMode = childParents[parentId]
  delete childParents[parentId]

  // Bypass: replace the removed edge with edges from the removed parent's
  // own parents (the grandparents) to the child, so the inheritance chain
  // is preserved — mirroring the bypass behavior of removeCalendarNode.
  const grandparents = normalizeGroupInheritanceMap(graph[parentId])
  Object.entries(grandparents).forEach(([grandparentId, mode]) => {
    if (
      removedMode === ParentGroupLinkMode.Propagating &&
      mode === ParentGroupLinkMode.Propagating &&
      grandparentId !== childId &&
      !childParents[grandparentId]
    ) {
      childParents[grandparentId] = ParentGroupLinkMode.Propagating
    }
  })

  return { ...graph, [childId]: childParents }
}

export function removeCalendarNode(
  graph: CalendarGraph,
  groupId: string
): CalendarGraph {
  const removedNodeParents = normalizeGroupInheritanceMap(graph[groupId])

  const result = Object.fromEntries(
    Object.entries(graph)
      .filter(([childId]) => childId !== groupId)
      .map(([childId, parentGroups]) => {
        const parents = { ...normalizeGroupInheritanceMap(parentGroups) }
        const hadRemovedParent =
          parents[groupId] === ParentGroupLinkMode.Propagating

        delete parents[groupId]

        if (hadRemovedParent) {
          Object.entries(removedNodeParents).forEach(([parentId, mode]) => {
            if (
              mode === ParentGroupLinkMode.Propagating &&
              parentId !== childId &&
              !parents[parentId]
            ) {
              parents[parentId] = ParentGroupLinkMode.Propagating
            }
          })
        }

        return [childId, parents]
      })
  )

  return result
}

export function changedChildIds(
  base: CalendarGraph,
  draft: CalendarGraph
): string[] {
  const childIds = new Set([...Object.keys(base), ...Object.keys(draft)])
  return Array.from(childIds).filter((childId) => {
    const baseParents = normalizeGroupInheritanceMap(base[childId])
    const draftParents = normalizeGroupInheritanceMap(draft[childId])
    return (
      Object.keys(baseParents).length !== Object.keys(draftParents).length ||
      Object.entries(baseParents).some(
        ([parentId, mode]) => draftParents[parentId] !== mode
      )
    )
  })
}

export function inheritancePayload(
  base: CalendarGraph,
  draft: CalendarGraph
): InheritFromGroupsRequest {
  return Object.fromEntries(
    changedChildIds(base, draft).map((childId) => [
      childId,
      normalizeGroupInheritanceMap(draft[childId])
    ])
  )
}

export function mergeGraphIssues(
  existingIssues: Record<string, string>,
  nextIssues: Array<{ parentId: string; childId: string; message: string }>
): Record<string, string> {
  return {
    ...existingIssues,
    ...Object.fromEntries(
      nextIssues.map((issue) => [
        `${issue.parentId}->${issue.childId}`,
        issue.message
      ])
    )
  }
}

// Returns a save payload containing the groups currently on the canvas with
// their full current parent map (complete picture for groups with parents).
// A group with an empty parent map is included only if its base graph had
// parents — i.e., the emptiness was produced during this editing session
// (a removal). This avoids sending parents-with-no-children that were never
// touched by the user (e.g., an upstream calendar the user does not own
// locally but which appeared on the canvas as a parent node).
export function fullInheritancePayload(
  base: CalendarGraph,
  draft: CalendarGraph,
  visibleGroupIds: Iterable<string>
): InheritFromGroupsRequest {
  return Object.fromEntries(
    Array.from(new Set(visibleGroupIds))
      .map(
        (childId) =>
          [childId, normalizeGroupInheritanceMap(draft[childId])] as const
      )
      .filter(([childId, draftParents]) => {
        if (Object.keys(draftParents).length > 0) return true
        // Empty draft parents: only send if base had parents, meaning the
        // user cleared them during this editing session.
        const baseParents = normalizeGroupInheritanceMap(base[childId])
        return Object.keys(baseParents).length > 0
      })
  )
}
